/**
 * AuditLogView (AUDIT-4, Seth 2026-06-26) — the admin-only "who changed what,
 * when" log across all of Pegasus, with one-click Undo on anything reversible.
 *
 * Fed by the `pegasus_audit` spine, which a database trigger writes on EVERY
 * change to a Pegasus-native table (so nothing is forgotten or bypassed; ESC
 * mirror tables are never touched — DEC-22). Reads are RLS-gated to admins;
 * Undo calls the security-definer `pegasus_audit_undo` RPC (also admin-only),
 * which reverses the change (restore prior values / re-insert a delete / remove
 * an insert) and re-audits the undo itself. Sends + payments are flagged
 * "can't be undone" — you'd send a correction or issue a refund instead.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  RotateCcw, Plus, Pencil, Trash2, Ban, ChevronDown, ChevronRight,
  Search, Loader2, ShieldAlert,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { InfoTip } from "@/components/InfoTip";
import { useStaffNames, staffFirst } from "@/lib/staffNames";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { formatDateTimeUS } from "@/lib/format";

interface AuditRow {
  id: number;
  at: string;
  actor: string | null;
  actor_uid: string | null;
  action: "insert" | "update" | "delete";
  entity: string;
  entity_id: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  changed_cols: string[] | null;
  reversible: boolean;
  undone_at: string | null;
  undone_by: string | null;
}

/** Friendly names for the tables a dispatcher would recognize.
 *  Anything missing falls back to a tidied table name ("pegasus_foo_bar" → "Foo Bar"). */
const ENTITY_LABEL: Record<string, string> = {
  // Customers & jobs
  pegasus_customer: "Customer",
  pegasus_customer_override: "Customer Edit",
  pegasus_customer_email: "Customer Email",
  pegasus_customer_merge: "Customer Merge",
  pegasus_customer_status: "Customer Status",
  pegasus_customer_payment_profile: "Saved Payment Method",
  pegasus_location: "Location",
  pegasus_owner: "Owner",
  pegasus_property: "Property",
  pegasus_property_ownership: "Ownership Change",
  pegasus_note: "Customer Note",
  pegasus_internal_note: "Internal Note",
  pegasus_followup: "Follow-Up",
  pegasus_dispatch: "Dispatch",
  dispatch_action: "Dispatch Action",
  dispatch_assignment: "Dispatch Assignment",
  pegasus_field_status: "Tech Status Update",
  pegasus_job: "Job",
  pegasus_job_equipment: "Job Equipment",
  pegasus_job_material: "Job Material",
  pegasus_job_labor: "Job Labor",
  pegasus_job_sales_credit: "Sales Credit",
  pegasus_change_order: "Change Order",
  pegasus_permit: "Permit",
  pegasus_permit_inspection: "Permit Inspection",
  pegasus_builder: "Builder Settings",
  pegasus_inspection_contact: "Inspection Contact",
  pegasus_equipment: "Equipment",
  pegasus_equipment_override: "Equipment Override",
  equipment_model_class: "Model Classification",
  pegasus_file: "File",
  pegasus_library_doc: "Library Document",
  pegasus_library_folder: "Library Folder",
  // Money
  pegasus_quote: "Proposal",
  pegasus_quote_option: "Proposal Option",
  pegasus_quote_line: "Proposal Line",
  pegasus_quote_discount: "Proposal Discount",
  pegasus_quote_rebate: "Proposal Rebate",
  pegasus_job_invoice: "Invoice",
  pegasus_job_invoice_line: "Invoice Line",
  pegasus_invoice_line_edit: "Invoice Line Edit",
  pegasus_esc_post: "ESC Posting",
  pegasus_payment: "Payment",
  pegasus_payment_allocation: "Payment Applied",
  pegasus_payment_refund: "Refund",
  pegasus_plan_sale: "Service Agreement",
  pegasus_plan_price: "Plan Price",
  pegasus_plan_config: "Plan Setup",
  pegasus_plan_accessory: "Plan Add-On",
  pegasus_financing_plan: "Financing Plan",
  pegasus_utility: "Utility",
  pegasus_sub_invoice: "Subcontractor Invoice",
  pegasus_subcontractor: "Subcontractor",
  pegasus_purchase: "Inventory Purchase",
  pegasus_po: "Purchase Order",
  pegasus_po_line: "PO Line",
  pegasus_pickup_item: "Parts Pickup",
  item: "Pricebook Item",
  fee_item: "Service Fee",
  plan_discount: "Discount Tier",
  // Scheduling
  agreement_visit: "Tune-Up Visit",
  pegasus_visit_day: "Tune-Up Day",
  pegasus_visit_day_stop: "Tune-Up Stop",
  pegasus_oncall_day: "On-Call Day",
  pegasus_oncall_period: "On-Call Schedule",
  pegasus_oncall_swap_request: "On-Call Swap",
  pegasus_install_day_override: "Install Day Change",
  pegasus_install_week_crew: "Install Crew",
  pegasus_install_week_hide: "Install Week Hide",
  pegasus_ops_expected_job: "Expected Job",
  pegasus_ops_alert: "Office Alert",
  portal_request: "Portal Request",
  // Messages
  message: "Message",
  pegasus_outbox: "Outgoing Message",
  pegasus_dm: "Team Message",
  pegasus_bulletin: "Bulletin",
  pegasus_template: "Email Template",
  reply_template: "Reply Template",
  pegasus_comms_automation: "Automation",
  pegasus_comms_pref: "Message Preference",
  contact_consent: "Contact Consent",
  contact: "Contact",
  pegasus_campaign: "Campaign",
  pegasus_audience: "Audience",
  segment: "Audience Segment",
  inbox_auto_archive: "Auto-Archive Sender",
  extracted_signal: "AI Customer Note",
  // Team & settings
  app_user: "User Login",
  app_role: "Access Role",
  team_role: "Job Title",
  tech_profile: "Tech Profile",
  tech_skill: "Tech Specialty",
  skill_domain: "Specialty",
  tech_pay: "Tech Pay",
  pegasus_tech_pay: "Pay Rate",
  tech_availability: "Tech Availability",
  service_type: "Service Type",
  equipment_category: "Equipment Category",
  lead_source: "Lead Source",
  pegasus_jurisdiction: "Permit City",
  org_setting: "Setting",
};

function entityLabel(entity: string): string {
  if (ENTITY_LABEL[entity]) return ENTITY_LABEL[entity];
  return entity
    .replace(/^pegasus_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Mid-sentence form: "Created this service type" — keeps acronyms like PO / ESC / AI intact. */
function entityLower(entity: string): string {
  return entityLabel(entity).split(" ").map((w) => (/^[A-Z]{2,}$/.test(w) ? w : w.toLowerCase())).join(" ");
}

const ACTION_META: Record<AuditRow["action"], { label: string; icon: typeof Plus; cls: string }> = {
  insert: { label: "Created", icon: Plus, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  update: { label: "Edited", icon: Pencil, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  delete: { label: "Deleted", icon: Trash2, cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

/** Compact, readable rendering of a stored value. */
function show(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "object") return JSON.stringify(v);
  const s = String(v);
  return s.length > 80 ? s.slice(0, 80) + "…" : s;
}

/** One-line "what happened" for the row. */
function summarize(r: AuditRow): string {
  if (r.action === "insert" && r.entity === "message") {
    const ch = r.after?.channel;
    return `Sent ${ch === "email" ? "an email" : ch === "sms" ? "a text" : "a message"} to the customer`;
  }
  if (r.action === "insert") return `Created this ${entityLower(r.entity)}`;
  if (r.action === "delete") return `Deleted this ${entityLower(r.entity)}`;
  const cols = (r.changed_cols ?? []).filter((c) => c !== "updated_at");
  if (cols.length === 0) return "Edited";
  if (cols.length <= 2) {
    return cols
      .map((c) => `${c.replace(/_/g, " ")}: ${show(r.before?.[c])} → ${show(r.after?.[c])}`)
      .join(" · ");
  }
  return `Edited ${cols.length} fields (${cols.slice(0, 3).map((c) => c.replace(/_/g, " ")).join(", ")}…)`;
}

const PAGE = 200;

export function AuditLogSection() {
  const qc = useQueryClient();
  const staff = useStaffNames().data;
  // PERM-ENFORCE-01: reading the log (audit.view) and reversing a change
  // (audit.undo) are two different powers; the RPC itself is admin-only today.
  const canUndo = useCan("audit.undo");
  const [entity, setEntity] = useState<string>("");
  const [action, setAction] = useState<string>("");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState<number | null>(null);
  const [undoErr, setUndoErr] = useState<{ id: number; msg: string } | null>(null);

  const query = useQuery({
    queryKey: ["audit_log", entity, action],
    queryFn: async (): Promise<AuditRow[]> => {
      let sel = supabase.from("pegasus_audit").select("*").order("at", { ascending: false }).limit(PAGE);
      if (entity) sel = sel.eq("entity", entity);
      if (action) sel = sel.eq("action", action);
      const { data, error } = await sel;
      if (error) throw error;
      return (data ?? []) as AuditRow[];
    },
  });

  const undo = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase.rpc("pegasus_audit_undo", { p_id: id });
      if (error) throw error;
    },
    onSuccess: () => {
      setUndoErr(null);
      qc.invalidateQueries({ queryKey: ["audit_log"] });
    },
    onError: (e: unknown, id) => {
      const msg = e instanceof Error ? e.message : (e as { message?: string })?.message ?? "Undo failed";
      setUndoErr({ id, msg });
    },
  });

  const rows = query.data ?? [];

  // The entity dropdown shows only what's actually present (so it's never a wall
  // of empty tables); searchable free-text narrows by person or record id.
  const entityOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.entity));
    return [...set].sort((a, b) => entityLabel(a).localeCompare(entityLabel(b)));
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (r) =>
        (r.actor ?? "").toLowerCase().includes(needle) ||
        (r.entity_id ?? "").toLowerCase().includes(needle) ||
        entityLabel(r.entity).toLowerCase().includes(needle) ||
        summarize(r).toLowerCase().includes(needle),
    );
  }, [rows, q]);

  const selCls = "h-9 rounded-md border border-hairline bg-white px-2.5 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30";

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-hairline bg-white p-5 shadow-e1">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search person, record, or change…"
              className={cn(selCls, "w-64 pl-8")}
            />
          </div>
          <select value={entity} onChange={(e) => setEntity(e.target.value)} className={selCls} aria-label="Record type">
            <option value="">All record types</option>
            {entityOptions.map((e) => (
              <option key={e} value={e}>{entityLabel(e)}</option>
            ))}
          </select>
          <select value={action} onChange={(e) => setAction(e.target.value)} className={selCls} aria-label="Action">
            <option value="">All actions</option>
            <option value="insert">Created</option>
            <option value="update">Edited</option>
            <option value="delete">Deleted</option>
          </select>
          {query.isFetching && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          <span className="ml-auto flex items-center gap-2 text-xs text-slate-400">
            {filtered.length}{rows.length === PAGE ? "+ " : " "}entries
            <InfoTip label="What Undo can reverse" align="end">
              Undo reverses a changed value, a delete or a status. Sent texts, emails and payments are recorded but can't be
              undone here. ESC is never touched.
            </InfoTip>
          </span>
        </div>
      </div>

      {/* The log */}
      <div className="overflow-hidden rounded-xl border border-hairline bg-white shadow-e1">
        {query.isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading the audit log…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-400">
            Nothing here yet — changes will appear the moment someone edits a record.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((r) => {
              const m = ACTION_META[r.action];
              const Icon = m.icon;
              const isOpen = expanded === r.id;
              const cols = (r.changed_cols ?? []).filter((c) => c !== "updated_at");
              return (
                <li key={r.id} className={cn("px-4 py-3", r.undone_at && "bg-slate-50/60")}>
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => setExpanded(isOpen ? null : r.id)}
                      className="mt-0.5 text-slate-300 transition-colors hover:text-slate-500"
                      aria-label={isOpen ? "Collapse" : "Expand details"}
                    >
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>

                    <span className={cn("mt-0.5 inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] font-semibold", m.cls)}>
                      <Icon className="h-3 w-3" /> {m.label}
                    </span>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
                        <span className="font-semibold text-slate-800">{entityLabel(r.entity)}</span>
                        {r.entity_id && <span className="font-mono text-[11px] text-slate-400">{r.entity_id.slice(0, 12)}</span>}
                      </div>
                      <div className="mt-0.5 text-sm text-slate-600">{summarize(r)}</div>
                      <div className="mt-0.5 text-[11px] text-slate-400">
                        {formatDateTimeUS(r.at)} · {staffFirst(staff, r.actor, r.actor_uid)}
                        {r.undone_at && (
                          <span className="ml-1 font-medium text-slate-500">· undone by {staffFirst(staff, r.undone_by)}</span>
                        )}
                      </div>

                      {isOpen && (
                        <div className="mt-2 rounded-lg border border-slate-100 bg-slate-50/70 p-3 text-xs">
                          {r.action === "update" ? (
                            <table className="w-full">
                              <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wide text-slate-400">
                                  <th className="pb-1 pr-3 font-semibold">Field</th>
                                  <th className="pb-1 pr-3 font-semibold">Before</th>
                                  <th className="pb-1 font-semibold">After</th>
                                </tr>
                              </thead>
                              <tbody className="align-top">
                                {cols.map((c) => (
                                  <tr key={c} className="border-t border-slate-100">
                                    <td className="py-1 pr-3 font-medium text-slate-600">{c.replace(/_/g, " ")}</td>
                                    <td className="py-1 pr-3 text-rose-700">{show(r.before?.[c])}</td>
                                    <td className="py-1 text-emerald-700">{show(r.after?.[c])}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words text-[11px] text-slate-600">
                              {JSON.stringify(r.action === "delete" ? r.before : r.after, null, 2)}
                            </pre>
                          )}
                        </div>
                      )}

                      {undoErr?.id === r.id && (
                        <div className="mt-1.5 flex items-center gap-1 text-[11px] text-rose-600">
                          <ShieldAlert className="h-3 w-3" /> {undoErr.msg}
                        </div>
                      )}
                    </div>

                    {/* Undo */}
                    <div className="shrink-0">
                      {r.undone_at ? (
                        <span className="text-[11px] font-medium text-slate-400">Undone</span>
                      ) : r.reversible && canUndo ? (
                        <button
                          type="button"
                          disabled={undo.isPending}
                          onClick={() => undo.mutate(r.id)}
                          className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2.5 py-1.5 text-xs font-semibold text-brand-700 transition-colors hover:bg-brand-50 disabled:opacity-50"
                        >
                          {undo.isPending && undo.variables === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                          Undo
                        </button>
                      ) : r.reversible ? null : (
                        <span title="A sent message or a payment can't be reversed here — send a correction or issue a refund instead." className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-medium text-slate-400">
                          <Ban className="h-3 w-3" /> can't undo
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {rows.length === PAGE && (
        <p className="text-center text-[11px] text-slate-400">
          Showing the most recent {PAGE}. Narrow with the filters to see older entries.
        </p>
      )}
    </div>
  );
}
